import { useEffect, useState } from "react";
import { apiAuth } from "@/lib/api";

const VIS_LABEL = {
  owner_only: "Nur Owner",
  team: "Team",
  owner_and_assistant: "Owner + Assistant",
};

export const NotesPanel = ({ entityType, entityId, currentUserId }) => {
  const apiBase = `/admin/${entityType}s/${entityId}/notes`;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState("");
  const [visibility, setVisibility] = useState("team");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiAuth.get(apiBase);
      setItems(data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const add = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setError(null); setSaving(true);
    try {
      await apiAuth.post(apiBase, { content: content.trim(), visibility, pinned: false });
      setContent("");
      await load();
    } catch (_e) { setError("Notiz konnte nicht gespeichert werden."); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Notiz wirklich löschen?")) return;
    try {
      await apiAuth.delete(`${apiBase}/${id}`);
      await load();
    } catch (_e) { setError("Löschen fehlgeschlagen."); }
  };

  return (
    <div className="flex flex-col gap-5" data-testid={`notes-${entityType}`}>
      <form onSubmit={add} className="bg-porcelain rounded-2xl p-4 border border-sandBeige">
        <textarea
          className="wit-input resize-none"
          rows={3}
          placeholder="Neue Notiz …"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          data-testid="note-input"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <select
            className="wit-input max-w-xs"
            value={visibility}
            onChange={(e) => setVisibility(e.target.value)}
            data-testid="note-visibility"
          >
            <option value="owner_only">Nur Owner</option>
            <option value="team">Team</option>
            <option value="owner_and_assistant">Owner + Assistant</option>
          </select>
          <div className="flex-1" />
          <button
            type="submit"
            disabled={saving || !content.trim()}
            className="wit-btn-primary disabled:opacity-60"
            data-testid="note-submit"
          >
            {saving ? "Wird gespeichert …" : "Notiz hinzufügen"}
          </button>
        </div>
        {error && <p className="text-sm text-coral mt-2">{error}</p>}
      </form>

      <div className="flex flex-col gap-3">
        {loading && <p className="text-sm text-warmGrey">Lade …</p>}
        {!loading && items.length === 0 && (
          <p className="text-sm text-warmGrey">Noch keine Notizen.</p>
        )}
        {items.map((n) => (
          <div key={n.id} className="bg-white border border-sandBeige rounded-2xl p-4" data-testid={`note-${n.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-cocoa whitespace-pre-wrap leading-relaxed">{n.content}</p>
                <p className="text-xs text-warmGrey mt-2">
                  {n.author_name} · {new Date(n.created_at).toLocaleString("de-DE")} · <span className="italic">{VIS_LABEL[n.visibility] || n.visibility}</span>
                </p>
              </div>
              {n.author_id === currentUserId && (
                <button type="button" onClick={() => del(n.id)} className="text-xs text-coral hover:underline">Löschen</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotesPanel;
